import axios from "axios";
import { MAIN_API } from "./apilist";
import { organizerManualCheckinReservationApi } from "./api";
import { store } from "../redux/store/store";

/* Cliente del organizador para Turnos Beauty: /api/organizer/beauty/*.
   Vive aparte de api.js; usa el axios global, así que el interceptor compartido (401/402) aplica igual. */
const BASE = MAIN_API + "/api/organizer/beauty";
const authHeader = () => ({ Authorization: `Bearer ${store.getState().auth.accessToken}` });
const multipart = () => ({ ...authHeader(), "Content-Type": "multipart/form-data" });

/* Salón activo del panel. Solo los negocios multi-sede lo fijan (selector de TurnosOrganizer): con un salón elegido,
   cada lectura/escritura que el back resuelve "por el salón del negocio" viaja con `salonId` (query en GET, body en POST).
   Sin salón fijado (todos los demás negocios) los pedidos salen exactamente como siempre. */
let salonActivo = null;
export const beautySetSalonActivo = (id) => {
  salonActivo = id ? String(id) : null;
};
const getConfig = (params) => {
  const todos = salonActivo ? { salonId: salonActivo, ...(params || {}) } : params;
  return todos ? { params: todos, headers: authHeader() } : { headers: authHeader() };
};
const conSalon = (body) => (salonActivo ? { ...body, salonId: salonActivo } : body);

// Salón (uno por negocio; `info` es null si todavía no hay)
export const beautyGetSalonApi = () => axios.get(`${BASE}/salon`, getConfig());
// Salones activos del negocio (selector del panel multi-sede)
export const beautyListSalonsApi = () => axios.get(`${BASE}/salons`, { headers: authHeader() });
export const beautyAddSalonApi = (formData) => axios.post(`${BASE}/salon/add`, formData, { headers: multipart() });
export const beautyUpdateSalonApi = (formData) => axios.post(`${BASE}/salon/update`, formData, { headers: multipart() });
export const beautyDeleteSalonApi = (id) => axios.post(`${BASE}/salon/delete`, { id }, { headers: authHeader() });
export const beautyToggleSalonApi = (id) => axios.post(`${BASE}/salon/toggle-status`, { id }, { headers: authHeader() });

// Servicios
// `salonId` explícito (cards de un negocio multi-sede) gana sobre el salón activo.
export const beautyListServicesApi = (salonId) => axios.get(`${BASE}/services`, getConfig(salonId ? { salonId } : undefined));
export const beautyAddServiceApi = (data) => axios.post(`${BASE}/services/add`, conSalon(data), { headers: authHeader() });
export const beautyUpdateServiceApi = (data) => axios.post(`${BASE}/services/update`, data, { headers: authHeader() });
export const beautyDeleteServiceApi = (id) => axios.post(`${BASE}/services/delete`, { id }, { headers: authHeader() });
export const beautyReorderServicesApi = (ids) => axios.post(`${BASE}/services/reorder`, conSalon({ ids }), { headers: authHeader() });

// Profesionales
export const beautyListProfessionalsApi = (salonId) =>
  axios.get(`${BASE}/professionals`, getConfig(salonId ? { salonId } : undefined));
export const beautyGetProfessionalApi = (id) => axios.get(`${BASE}/professionals/${id}`, { headers: authHeader() });
export const beautyAddProfessionalApi = (data) => axios.post(`${BASE}/professionals/add`, conSalon(data), { headers: authHeader() });
export const beautyUpdateProfessionalApi = (data) => axios.post(`${BASE}/professionals/update`, data, { headers: authHeader() });
export const beautyDeleteProfessionalApi = (id) => axios.post(`${BASE}/professionals/delete`, { id }, { headers: authHeader() });
export const beautyReorderProfessionalsApi = (ids) =>
  axios.post(`${BASE}/professionals/reorder`, conSalon({ ids }), { headers: authHeader() });
// Solo dueño: vincula una cuenta de Zoco de su equipo; `identificador` es el usuario o el correo.
export const beautyLinkAccountApi = (professionalId, identificador) =>
  axios.post(`${BASE}/professionals/link-account`, { professionalId, identificador }, { headers: authHeader() });
export const beautyUnlinkAccountApi = (professionalId) =>
  axios.post(`${BASE}/professionals/unlink-account`, { professionalId }, { headers: authHeader() });
// Solo dueño: crea una cuenta de Zoco nueva y la vincula al profesional.
export const beautyAssignableRolesApi = () =>
  axios.get(`${BASE}/professionals/assignable-roles`, { headers: authHeader() });
// `puntosVentaIds` (opcional) viaja solo si trae algo: sin locales elegidos el body queda como siempre.
export const beautyCreateAccountApi = ({ professionalId, nombre, nombreUsuario, password, rolId, puntosVentaIds }) =>
  axios.post(
    `${BASE}/professionals/create-account`,
    {
      professionalId,
      nombre,
      nombreUsuario,
      password,
      rolId,
      ...(Array.isArray(puntosVentaIds) && puntosVentaIds.length > 0 ? { puntosVentaIds } : {}),
    },
    { headers: authHeader() }
  );
// Solo dueño: locales (puntos de venta) del PDV del negocio y los que tiene asignados cada profesional.
export const beautyPdvPointsApi = () => axios.get(`${BASE}/professionals/pdv-points`, { headers: authHeader() });
export const beautyProfessionalPdvPointsApi = (professionalId) =>
  axios.get(`${BASE}/professionals/${professionalId}/pdv-points`, { headers: authHeader() });
export const beautyUpdateProfessionalPdvPointsApi = (professionalId, puntosVentaIds) =>
  axios.put(`${BASE}/professionals/${professionalId}/pdv-points`, { puntosVentaIds }, { headers: authHeader() });

// Disponibilidad y agenda
// `items` = [{ serviceId, variantId?, addOnIds? }]; GET routes take it as ONE query param with the JSON (axios encodes it).
export const beautyAvailabilityApi = ({ date, items = [], professionalId } = {}) =>
  axios.get(`${BASE}/availability`, getConfig({ date, items: JSON.stringify(items), ...(professionalId ? { professionalId } : {}) }));
export const beautyAgendaApi = (date, { includeCancelled = false } = {}) =>
  axios.get(`${BASE}/agenda`, getConfig({ date, ...(includeCancelled ? { includeCancelled: true } : {}) }));

// Turnos
export const beautyListAppointmentsApi = (params = {}) =>
  axios.get(`${BASE}/appointments`, getConfig(params));
export const beautyAppointmentClientApi = (reservationId) =>
  axios.get(`${BASE}/appointments/${reservationId}/client`, getConfig());
export const beautyCreateAppointmentApi = (data, idempotencyKey) =>
  axios.post(`${BASE}/appointments`, conSalon(data), {
    headers: { ...authHeader(), ...(idempotencyKey ? { "Idempotency-Key": idempotencyKey } : {}) },
  });
export const beautyConfirmAppointmentApi = (reservationId) =>
  axios.post(`${BASE}/appointments/${reservationId}/confirm`, {}, { headers: authHeader() });
export const beautyCancelAppointmentApi = (reservationId) =>
  axios.post(`${BASE}/appointments/${reservationId}/cancel`, {}, { headers: authHeader() });
export const beautyNoShowAppointmentApi = (reservationId) =>
  axios.post(`${BASE}/appointments/${reservationId}/no-show`, {}, { headers: authHeader() });
// Check-in manual: el endpoint de scanner es el mismo que usa Canchas (no filtra por vertical).
export const beautyCheckinAppointmentApi = (reservationId) => organizerManualCheckinReservationApi(reservationId);

// Mover un turno: horarios libres (opcionalmente con otro profesional) y reagendado.
export const beautyRescheduleOptionsApi = (reservationId, { date, professionalId } = {}) =>
  axios.get(`${BASE}/appointments/${reservationId}/reschedule-options`, getConfig({ date, ...(professionalId ? { professionalId } : {}) }));
export const beautyRescheduleAppointmentApi = (reservationId, { date, slotStart, professionalId } = {}) =>
  axios.post(
    `${BASE}/appointments/${reservationId}/reschedule`,
    conSalon({ date, slotStart, ...(professionalId ? { professionalId } : {}) }),
    { headers: authHeader() }
  );

// Bloqueos (el back pide `barId` = id del salón en el alta)
export const beautyCreateBlockApi = (data) => axios.post(`${BASE}/blocks`, data, { headers: authHeader() });
export const beautyDeleteBlockApi = (blockId) => axios.delete(`${BASE}/blocks/${blockId}`, { headers: authHeader() });

// Comisiones (solo dueño) y perfil del profesional vinculado
export const beautyGetCommissionsApi = () => axios.get(`${BASE}/commissions`, getConfig());
export const beautySaveCommissionsApi = (body) => axios.post(`${BASE}/commissions`, conSalon(body), { headers: authHeader() });
export const beautyMeApi = () => axios.get(`${BASE}/me`, getConfig());

// Ficha de cliente (el negocio tiene un registro por cliente). Todas devuelven la respuesta de axios: `data.info` trae
// el resultado y un error trae `response.data.{ message, codigo }` (invalid_field, too_many_tags, note_too_long,
// field_required, solo_dueno, profesional_vinculado, not_note_author, account_appointment).
export const beautyListClientsApi = ({ q, page, birthdayMonth, duplicates, archived, consent } = {}) =>
  axios.get(`${BASE}/clients`, {
    params: {
      ...(q ? { q } : {}),
      ...(page > 1 ? { page } : {}),
      ...(birthdayMonth ? { birthdayMonth } : {}),
      ...(duplicates ? { duplicates: 1 } : {}),
      ...(archived ? { archived: true } : {}),
      ...(consent ? { consent } : {}),
    },
    headers: authHeader(),
  });
export const beautyGetClientApi = (clientId) => axios.get(`${BASE}/clients/${clientId}`, { headers: authHeader() });
export const beautyUpdateClientApi = (clientId, data) => axios.patch(`${BASE}/clients/${clientId}`, data, { headers: authHeader() });
export const beautyArchiveClientApi = (clientId) => axios.post(`${BASE}/clients/${clientId}/archive`, {}, { headers: authHeader() });
export const beautyUnarchiveClientApi = (clientId) => axios.post(`${BASE}/clients/${clientId}/unarchive`, {}, { headers: authHeader() });
// Fusionar: la ficha `clientId` (la que queda) absorbe a las de `sourceIds` (1 a 20); devuelve la ficha resultante como GET /clients/:id.
export const beautyMergeClientApi = (clientId, sourceIds) =>
  axios.post(`${BASE}/clients/${clientId}/merge`, { sourceIds }, { headers: authHeader() });
export const beautyClientAppointmentsApi = (clientId) =>
  axios.get(`${BASE}/clients/${clientId}/appointments`, { headers: authHeader() });
// Desde un turno: abrir/crear la ficha (un turno de invitado necesita `{ create: true }`), vincularlo a otra o desvincularlo (solo dueño).
export const beautyOpenAppointmentClientApi = (reservationId, body = {}) =>
  axios.post(`${BASE}/appointments/${reservationId}/client`, body, { headers: authHeader() });
export const beautyLinkAppointmentClientApi = (reservationId, clientId) =>
  axios.post(`${BASE}/appointments/${reservationId}/client/link`, { clientId }, { headers: authHeader() });
export const beautyUnlinkAppointmentClientApi = (reservationId) =>
  axios.delete(`${BASE}/appointments/${reservationId}/client/link`, { headers: authHeader() });
// Notas de la ficha: `kind` es "general" o "technical".
export const beautyClientNotesApi = (clientId) => axios.get(`${BASE}/clients/${clientId}/notes`, { headers: authHeader() });
export const beautyAddClientNoteApi = (clientId, { kind, body }) =>
  axios.post(`${BASE}/clients/${clientId}/notes`, { kind, body }, { headers: authHeader() });
export const beautyUpdateClientNoteApi = (clientId, noteId, data) =>
  axios.patch(`${BASE}/clients/${clientId}/notes/${noteId}`, data, { headers: authHeader() });
export const beautyArchiveClientNoteApi = (clientId, noteId) =>
  axios.post(`${BASE}/clients/${clientId}/notes/${noteId}/archive`, {}, { headers: authHeader() });

// Turnos fijos (series): cada N semanas, mismo profesional, servicios y horario.
// `body` = { professionalId, items, date, slotStart, intervalWeeks, dateTo } (+ name, phone, email?, notes? al crear).
export const beautyPreviewSeriesApi = (body) => axios.post(`${BASE}/series/preview`, conSalon(body), { headers: authHeader() });
export const beautyCreateSeriesApi = (body) => axios.post(`${BASE}/series`, conSalon(body), { headers: authHeader() });
export const beautyListSeriesApi = () => axios.get(`${BASE}/series`, getConfig());
// `from` (YYYY-MM-DD) cancela esa fecha y las siguientes; sin `from` el back usa hoy.
export const beautyCancelSeriesApi = (seriesId, from) =>
  axios.post(`${BASE}/series/${seriesId}/cancel`, from ? { from } : {}, { headers: authHeader() });
